import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Badge } from "../../ui/badge";
import {
  Search,
  Download,
  TrendingUp,
  TrendingDown,
  Wallet,
  Receipt,
  Users,
  ArrowDownCircle,
  ArrowUpCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  getApprovedTourOperators,
  getWalletLedger,
  getWalletList,
  getWalletSummary,
  getWalletSummaryByTO,
  TourOperatorApi,
  WalletSummaryApiResponse,
  getBaseCurrency,
  exportWalletLedger,
} from "../../services/toLedgerWalletService";
import { FullScreenLoading } from "../../components/ui/loader";
import Pagination from "../../ui/pagination";
import { useAuth } from "../../contexts/AuthContext";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";

interface Transaction {
  id: string;
  rawDate: string;
  date: string;
  type: "debit" | "credit";
  description: string;
  debit: number;
  credit: number;
  balance: number;
  receiptNo?: string;
  reference?: string;
  applicationNo?: string;
}

interface WalletLedgerApi {
  transactionDate: string;
  creditAmount: number;
  debitAmount: number;
  balanceAfter: number;
  remarks: string;
  transactionType: string;
  referenceId?: string;
}

interface TOLedgerWalletFinanceProps {
  onNavigate: (page: string) => void;
  viewMode?: "wallet-only" | "ledger-only";
}

export const TOLedgerWalletFinance = ({
  onNavigate,
  viewMode = "wallet-only",
}: TOLedgerWalletFinanceProps) => {
  const { setPageHeader } = usePageHeader();
  const [selectedTOId, setSelectedTOId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tourOperatorSearch, setTourOperatorSearch] = useState("");
  const [ledgerTransactions, setLedgerTransactions] = useState<Transaction[]>(
    [],
  );

  const [walletSummary, setWalletSummary] =
    useState<WalletSummaryApiResponse | null>(null);

  const [currencySymbol, setCurrencySymbol] = useState<string>("$");
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tourOperators, setTourOperators] = useState<TourOperatorApi[]>([]);
  const [sortColumn] = useState<string>("date");
  const [sortDirection] = useState<"asc" | "desc">("desc");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [walletCurrentBalance, setWalletCurrentBalance] = useState(0);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const isFilterApplied = Boolean(searchTerm || dateFrom || dateTo);

  const { user } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  const tourOperatorId =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (user as any)?.tourOperatorId ?? null;

  const isTourOperator = !!tourOperatorId;
  const { canAdd, screenName, icon: ScreenIcon } = useScreenPermission();

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    setPageNo(1);
  }, [searchTerm, dateFrom, dateTo, selectedTOId]);

  const handleResetLedger = () => {
    setSelectedTOId("");
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
    setLedgerTransactions([]);
  };

  useEffect(() => {
    getTourOperatorData();
  }, []);

  useEffect(() => {
    const loadBaseCurrency = async () => {
      try {
        setLoading(true);
        const res = await getBaseCurrency();
        setCurrencySymbol(res.currencySymbol || "$");
      } catch (error) {
        console.error("Failed to load base currency", error);
      } finally {
        setLoading(false);
      }
    };

    loadBaseCurrency();
  }, []);

  const getTourOperatorData = async () => {
    try {
      setLoading(true);

      const res = await getApprovedTourOperators();
      console.log("Approved Tour Operators:", res);
      setTourOperators(res);
    } catch (err) {
      console.error("Error fetching approved tour operators:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTourOperators = tourOperators.filter((to) => {
    const fullName =
      `${to.firstName ?? ""} ${to.middleName ?? ""} ${to.lastName ?? ""}`.toLowerCase();
    const itsNumber = (to.itsNumber ?? "").toString().toLowerCase();
    const officeName = (to.officeName ?? "").toLowerCase();
    const search = (tourOperatorSearch ?? "").toLowerCase();

    return (
      fullName.includes(search) ||
      itsNumber.includes(search) ||
      officeName.includes(search)
    );
  });

  const selectedTourOperator = tourOperators.find(
    (to) => to.tourOperatorId.toString() === selectedTOId,
  );

  // const selectedTourOperatorName = selectedTourOperator
  // ? `${selectedTourOperator.firstName} ${selectedTourOperator.middleName ?? ""} ${selectedTourOperator.lastName}`
  // : "";

  const selectedTourOperatorName = selectedTourOperator
  ? selectedTourOperator.officeName ?? ""
  : "";

  const filteredTransactions = ledgerTransactions.filter((txn) => {
    const description = (txn.description ?? "").toLowerCase();
    const receiptNo = (txn.receiptNo ?? "").toLowerCase();
    const search = (searchTerm ?? "").toLowerCase();

    const matchesSearch =
      description.includes(search) || receiptNo.includes(search);

    const txnDate = new Date(txn.date);
    const matchesDateFrom = !dateFrom || txnDate >= new Date(dateFrom);
    const matchesDateTo = !dateTo || txnDate <= new Date(dateTo);

    return matchesSearch && matchesDateFrom && matchesDateTo;
  });

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: number;
    let bValue: number;

    switch (sortColumn) {
      case "date":
        aValue = new Date(a.rawDate).getTime();
        bValue = new Date(b.rawDate).getTime();
        break;
      case "debit":
        aValue = a.debit;
        bValue = b.debit;
        break;
      case "credit":
        aValue = a.credit;
        bValue = b.credit;
        break;
      case "balance":
        aValue = a.balance;
        bValue = b.balance;
        break;
      default:
        return 0;
    }

    return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const totalItems = totalCount;

  // const paginatedTransactions = sortedTransactions.slice(
  //   (currentPage - 1) * itemsPerPage,
  //   currentPage * itemsPerPage
  // );

  const hasLedgerData = ledgerTransactions.length > 0;
  // const hasFilteredResults = sortedTransactions.length > 0;

  const totalCredit = filteredTransactions.reduce(
    (sum, t) => sum + t.credit,
    0,
  );

  const totalDebit = filteredTransactions.reduce((sum, t) => sum + t.debit, 0);

  const isDateFilterApplied = Boolean(dateFrom || dateTo);

  const actualBalance =
    ledgerTransactions.length > 0
      ? ledgerTransactions[ledgerTransactions.length - 1].balance
      : 0;

  const filteredBalance =
    filteredTransactions.length > 0
      ? filteredTransactions[filteredTransactions.length - 1].balance
      : 0;

  const ledgerFilteredBalance = totalCredit - totalDebit;

  const displayedBalance =
    viewMode === "ledger-only"
      ? isFilterApplied
        ? ledgerFilteredBalance
        : actualBalance
      : isDateFilterApplied
        ? filteredBalance
        : actualBalance;

  const walletBalance = walletSummary?.grandTotalBalance ?? 0;
  const walletTotalCredit = walletSummary?.grandTotalCredit ?? 0;
  const walletTotalDebit = walletSummary?.grandTotalDebit ?? 0;

  const walletEffectiveBalance = walletBalance;
  const walletEffectiveCredit = walletTotalCredit;
  const walletEffectiveDebit = walletTotalDebit;

  const formatLedgerDate = (iso: string) => {
    if (!iso) return "";

    const d = new Date(iso);

    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
  };

  const mapLedgerApiToTransaction = (
    api: WalletLedgerApi,
    index: number,
  ): Transaction => {
    const isCredit = api.creditAmount > 0;

    return {
      id: `${index}`,
      rawDate: api.transactionDate,
      date: formatLedgerDate(api.transactionDate),
      type: isCredit ? "credit" : "debit",

      description: isCredit ? "" : api.remarks ?? "-",

      debit: api.debitAmount,
      credit: api.creditAmount,
      balance: api.balanceAfter,

      receiptNo: isCredit ? api.referenceId ?? undefined : undefined,
    };
  };

  useEffect(() => {
    if (viewMode !== "wallet-only") return;

    const loadWalletData = async () => {
      try {
        setLoadingLedger(true);

        if (isTourOperator && tourOperatorId) {
          const summaryRes = await getWalletSummaryByTO(tourOperatorId);

          setWalletSummary({
            totalWallets: 1,
            grandTotalBalance: summaryRes.currentBalance,
            grandTotalCredit: summaryRes.totalCredit,
            grandTotalDebit: summaryRes.totalDebit,
            totalTransactions: summaryRes.totalTransactions,
            records: [summaryRes],
          });

          const ledgerRes = await getWalletLedger(tourOperatorId);

          setLedgerTransactions(ledgerRes.map(mapLedgerApiToTransaction));

          setTotalCount(ledgerRes.length);
        } else {
          // ✅ ADMIN FLOW

          const summaryRes = await getWalletSummary();
          setWalletSummary(summaryRes);

          const listRes = await getWalletList({
            pageNo,
            pageSize,
          });

          setLedgerTransactions(listRes.items.map(mapLedgerApiToTransaction));

          setTotalCount(listRes.totalCount);
        }
      } catch (error) {
        console.error("Failed to load wallet data", error);
      } finally {
        setLoadingLedger(false);
      }
    };

    loadWalletData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, pageNo, pageSize, isTourOperator, tourOperatorId]);

  useEffect(() => {
    if (!selectedTOId || viewMode !== "ledger-only") return;

    const loadLedger = async () => {
      try {
        setLoadingLedger(true);
        const res = await getWalletLedger(Number(selectedTOId));
        setLedgerTransactions(res.map(mapLedgerApiToTransaction));
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingLedger(false);
      }
    };

    loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTOId, viewMode]);

  const resetFilters = () => {
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
    
  };

  useEffect(() => {
    resetFilters();
    setLedgerTransactions([]);
    if (viewMode === "wallet-only") {
      setSelectedTOId("");
    }
  }, [viewMode]);

  const handleExportLedger = async () => {
    try {
      setLoading(true);
      if (!selectedTOId) {
        toast.error("Please select a tour operator first.");
        return;
      }

      const blob = await exportWalletLedger(Number(selectedTOId));

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `Ledger_${selectedTourOperatorName || selectedTOId}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success("Ledger exported successfully!");
    } catch (error) {
      toast.dismiss();
      console.error("Export failed:", error);
      toast.error("Failed to export ledger. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Detect tour operator mode

  // ✅ If tour operator → calculate balance from ledger
  // const tourOperatorLedgerBalance =
  //   ledgerTransactions.length > 0
  //     ? ledgerTransactions[ledgerTransactions.length - 1].balance
  //     : 0;

  // ✅ FINAL effective balance (do NOT break existing logic)
  // const finalWalletBalance = isTourOperator && tourOperatorId
  //   ? tourOperatorLedgerBalance
  //   : walletEffectiveBalance;

  const finalWalletBalance = isTourOperator
    ? (walletSummary?.grandTotalBalance ?? 0)
    : walletEffectiveBalance;

  // Wallet Only View (Visa > Wallet)
  if (viewMode === "wallet-only") {
    return (
      <>
        {loading && <FullScreenLoading />}
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* <div>
              <h1
                style={{ color: "#5B9BD5" }}
                className="text-2xl md:text-3xl flex items-center gap-2"
              >
                <Wallet className="w-7 h-7" />
                  {screenName}
              </h1>
              <p className="text-gray-500 mt-1">
                View your wallet balance and transaction history
              </p>
            </div> */}
            {/* <Button
              onClick={handleExportLedger}
              className="text-white shadow-lg hover:opacity-90"
              style={{ backgroundColor: "#5B9BD5" }}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Statement
            </Button> */}
          </div>

          {/* Balance Cards */}
          <div style={{ marginTop: "0px" }}>
            {/* Available Balance */}
            <Card
              className="bg-gradient-to-br from-[#5B9BD5] to-[#4A8BC2] text-white shadow-xl col-span-1 md:col-span-2"
              style={{ gap: "0px", marginTop: "0px" }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-lg">
                    Available Balance
                  </CardTitle>
                  <Wallet className="w-6 h-6 text-white/80" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-4xl text-white md:text-5xl mb-2">
                  {currencySymbol}
                  {finalWalletBalance.toLocaleString()}
                </div>
                <p className="text-white/80 text-sm">
                  Ready to use for visa applications
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2" style={{ marginTop: "0px" }}>
            <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-blue-50">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-gray-900">
                      {walletSummary?.totalTransactions.toLocaleString() ?? 0}
                    </p>
                    <p className="text-xs text-gray-500">Last 30 days</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-green-50">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-gray-900">
                      {currencySymbol}
                      {isTourOperator
                        ? (walletSummary?.grandTotalCredit?.toLocaleString() ?? 0)
                        : walletEffectiveCredit.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Total Credits</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-red-50">
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-gray-900">
                      {currencySymbol}
                      {isTourOperator
                        ? (walletSummary?.grandTotalDebit?.toLocaleString() ?? 0)
                        : walletEffectiveDebit.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Total Debits</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Transaction History */}
          <Card
            className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
            style={{ gap: "0px", marginTop: "10px" }}
          >
            <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50 pb-6">
              <div className="space-y-4">
                <CardTitle style={{ color: "#5B9BD5" }}>
                  Transaction History
                </CardTitle>
                <div className="flex flex-col sm:flex-row gap-2 items-end">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search transactions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 whitespace-nowrap">
                      From Date:
                    </label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      placeholder="From"
                      className="w-full sm:w-auto"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 whitespace-nowrap">
                      To Date:
                    </label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      placeholder="To"
                      className="w-full sm:w-auto"
                    />
                  </div>
                  {isFilterApplied && (
                    <Button
                      variant="outline"
                      onClick={resetFilters}
                      className="border-gray-300 text-gray-600 hover:bg-gray-100 whitespace-nowrap"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent style={{ padding: "0px 0px 0px 0px" }}>
              {filteredTransactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 text-[#5B9BD5]">
                  {/* <span className="font-medium text-sm">
                    No transactions found
                  </span> */}
                  <div className="py-16 text-center text-gray-500">
                    <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-medium">No transactions found</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Try adjusting your filters
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {sortedTransactions.map((txn) => (
                    <div
                      key={txn.id}
                      className="p-4 hover:bg-gray-50 transition-colors"
                    >
                      {/* Desktop View - Grid Layout */}
                      <div className="hidden sm:grid grid-cols-12 gap-4 items-start">
                        {/* Date & Icon - 3 cols */}
                      <div className="col-span-4 flex items-start gap-3">
                        <div
                          className={`p-2 rounded-lg ${
                            txn.type === "credit" ? "bg-green-100" : "bg-red-100"
                          }`}
                        >
                          {txn.type === "credit" ? (
                            <ArrowDownCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <ArrowUpCircle className="w-4 h-4 text-red-600" />
                          )}
                        </div>

                        <div className="flex flex-col">
                          <p className="text-xs text-gray-600">Created On: {txn.date}</p>

                          
                        </div>
                      </div>
                        {/* Description + Badges - 5 cols */}
                        <div className="col-span-4">
                          <p className="text-sm text-gray-900 mb-1">
                            {txn.description}
                          </p>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {txn.applicationNo && (
                              <Badge className="text-xs bg-sky-100 text-sky-700 border-sky-300 px-2 py-0.5">
                                {txn.applicationNo}
                              </Badge>
                            )}
                            {txn.type === "credit" && txn.receiptNo && (
                              <div className="text-xs flex items-center gap-2 mt-1">
                                <span className="text-gray-500">Receipt No:</span>
                                <Badge className="text-xs bg-green-100 text-green-700 border-green-300 px-2 py-0.5">
                                  {txn.receiptNo}
                                </Badge>
                              </div>
                            )}
                            {txn.reference && (
                              <Badge className="mt-1 w-fit text-xs bg-green-100 text-green-700 border-green-300 px-2 py-0.5">
                                {txn.reference}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Debit Column - 2 cols */}
                        <div className="col-span-2 text-right">
                          {txn.debit > 0 ? (
                            <p className="text-sm text-red-600 font-medium">
                              {currencySymbol}
                              {txn.debit.toLocaleString()}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400">-</p>
                          )}
                        </div>

                        {/* Credit Column - 2 cols */}
                        <div className="col-span-2 text-right">
                          {txn.credit > 0 ? (
                            <p className="text-sm text-green-600 font-medium">
                              {currencySymbol}
                              {txn.credit.toLocaleString()}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400">-</p>
                          )}
                        </div>
                      </div>

                      {/* Mobile View - Card Layout */}
                      <div className="sm:hidden space-y-3">
                        {/* Header: Icon, Date, Type */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className={`p-2 rounded-lg ${txn.type === "credit" ? "bg-green-100" : "bg-red-100"}`}
                            >
                              {txn.type === "credit" ? (
                                <ArrowDownCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <ArrowUpCircle className="w-4 h-4 text-red-600" />
                              )}
                            </div>
                            <p className="text-xs text-gray-600">{txn.date}</p>
                          </div>
                          <Badge
                            variant={
                              txn.type === "credit" ? "default" : "destructive"
                            }
                            className="text-xs"
                          >
                            {txn.type === "credit" ? "Credit" : "Debit"}
                          </Badge>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-gray-900">{txn.description}</p>

                        {/* Badges */}
                        {(txn.applicationNo ||
                          txn.receiptNo ||
                          txn.reference) && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {txn.applicationNo && (
                              <Badge className="text-xs bg-sky-100 text-sky-700 border-sky-300 px-2 py-0.5">
                                {txn.applicationNo}
                              </Badge>
                            )}
                            {txn.receiptNo && (
                              <Badge className="text-xs bg-green-100 text-green-700 border-green-300 px-2 py-0.5">
                                {txn.receiptNo}
                              </Badge>
                            )}
                            {txn.reference && (
                              <Badge className="text-xs bg-yellow-100 text-yellow-700 border-yellow-300 px-2 py-0.5">
                                {txn.reference}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Amount Details */}
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Debit</p>
                            {txn.debit > 0 ? (
                              <p className="text-sm text-red-600 font-medium">
                                {currencySymbol}
                                {txn.debit.toLocaleString()}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-400">-</p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Credit</p>
                            {txn.credit > 0 ? (
                              <p className="text-sm text-green-600 font-medium">
                                {currencySymbol}
                                {txn.credit.toLocaleString()}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-400">-</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Total Row */}
                  <div className="mt-3 pb-3">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-t-2 border-gray-300">
                      <div className="p-4">
                        {/* Desktop Total Row */}
                        <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                          {/* Empty space for date/icon - 3 cols */}
                          <div className="col-span-3"></div>

                          {/* Total Label - 5 cols */}
                          <div className="col-span-5">
                            <p className="text-sm font-bold text-gray-900">
                              TOTAL
                            </p>
                          </div>

                          {/* Total Debit - 2 cols */}
                          <div className="col-span-2 text-right">
                            <p className="text-sm text-red-700 font-bold">
                              {currencySymbol}
                              {isTourOperator
                                ? ledgerTransactions
                                    .reduce((sum, t) => sum + t.debit, 0)
                                    .toLocaleString()
                                : walletEffectiveDebit.toLocaleString()}
                            </p>
                          </div>

                          {/* Total Credit - 2 cols */}
                          <div className="col-span-2 text-right">
                            <p className="text-sm text-green-700 font-bold">
                              {currencySymbol}
                              {isTourOperator
                                ? ledgerTransactions
                                    .reduce((sum, t) => sum + t.credit, 0)
                                    .toLocaleString()
                                : walletEffectiveCredit.toLocaleString()}
                            </p>
                          </div>
                        </div>

                        {/* Mobile Total Row */}
                        <div className="sm:hidden space-y-3">
                          <p className="text-sm font-bold text-gray-900">TOTAL</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs text-gray-600 mb-1">
                                Total Debit
                              </p>
                              <p className="text-sm text-red-700 font-bold">
                                {currencySymbol}
                                {totalDebit.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600 mb-1">
                                Total Credit
                              </p>
                              <p className="text-sm text-green-700 font-bold">
                                {currencySymbol}
                                {totalCredit.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Balance Row */}
                  <div>
                    <div className="bg-gradient-to-r from-sky-50 to-blue-100 border-t-2 border-[#5B9BD5]">
                      <div className="p-4">
                        {/* Desktop Balance Row */}
                        <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                          {/* Empty space - 3 cols */}
                          <div className="col-span-3"></div>

                          {/* Balance Label - 5 cols */}
                          <div className="col-span-5 flex items-center gap-3">
                            <div
                              className="p-2 rounded-lg"
                              style={{ backgroundColor: "#5B9BD5" }}
                            >
                              <Wallet className="w-4 h-4 text-white" />
                            </div>
                            <p
                              className="text-sm font-bold"
                              style={{ color: "#5B9BD5" }}
                            >
                              CURRENT BALANCE
                            </p>
                          </div>

                          {/* Empty - 2 cols */}
                          <div className="col-span-2"></div>

                          {/* Current Balance Amount - 2 cols */}
                          <div className="col-span-2 text-right">
                            <p
                              className="text-sm font-bold"
                              style={{ color: "#5B9BD5" }}
                            >
                              {currencySymbol}
                              {finalWalletBalance.toLocaleString()}
                            </p>
                          </div>
                        </div>

                        {/* Mobile Balance Row */}
                        <div className="sm:hidden flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className="p-2 rounded-lg"
                              style={{ backgroundColor: "#5B9BD5" }}
                            >
                              <Wallet className="w-4 h-4 text-white" />
                            </div>
                            <p
                              className="text-sm font-bold"
                              style={{ color: "#5B9BD5" }}
                            >
                              CURRENT BALANCE
                            </p>
                          </div>
                          <p
                            className="text-sm font-bold"
                            style={{ color: "#5B9BD5" }}
                          >
                            {currencySymbol}
                            {filteredBalance.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* {totalCount > itemsPerPage && ( */}
                <Pagination
                  totalItems={filteredTransactions.length}
                  itemsPerPage={pageSize}
                  currentPage={pageNo}
                  onPageChange={(page) => setPageNo(page)}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPageNo(1);
                  }}
                  label="transactions"
                />
              {/* )} */}
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Ledger Only View (Finance > TO Ledger)
  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center gap-4">
          {/* <div>
            <h1
              style={{ color: "#5B9BD5" }}
              className="flex items-center gap-2"
            >
              <Receipt className="w-7 h-7" />
              {screenName}
            </h1>
            <p className="text-gray-500 mt-1">
              View and manage tour operator financial records
            </p>
          </div> */}
          <div className="flex gap-2">
            {/* Reset when no transactions */}
            {selectedTOId &&
              !loadingLedger &&
              sortedTransactions.length === 0 && (
                <Button
                  variant="outline"
                  onClick={handleResetLedger}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  Clear Selection
                </Button>
              )} 

            {/* Export only when transactions exist */}
            {canAdd && selectedTOId && sortedTransactions.length > 0 && (
              <Button
                onClick={handleExportLedger}
                className="text-white shadow-lg hover:opacity-90"
                style={{ backgroundColor: "#5B9BD5" }}
              >
                <Download className="w-4 h-4 mr-2" />
                Export Ledgers
              </Button>
            )}
          </div>
        </div>

        {/* Tour Operator Selection */}
        <Card
          className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
          style={{ gap: "0px", marginTop: "0px" }}
        >
          <CardHeader
            className="border-b border-gray-100"
            style={{ gap: "0px", padding: "15px", marginTop: "10px" }}
          >
            <CardTitle style={{ color: "#5B9BD5" }}>
              Select Tour Operator
            </CardTitle>
          </CardHeader>
          <CardContent style={{ padding: "15px" }}>
            <Select
              value={selectedTOId}
              onValueChange={(value) => {
                setSelectedTOId(value);
                setTourOperatorSearch("");
              }}
            >
              <SelectTrigger className="border-gray-300">
                <SelectValue placeholder="Select Tour Operator to view ledger">
                  {selectedTourOperatorName ||
                    "Select Tour Operator to view ledger"}
                </SelectValue>
              </SelectTrigger>

              <SelectContent
                className="max-h-80 overflow-auto"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <div className="p-2 sticky top-0 bg-white z-10 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search tour operator..."
                      value={tourOperatorSearch}
                      onChange={(e) => setTourOperatorSearch(e.target.value)}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyUp={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyPress={(e) => {
                        e.stopPropagation();
                      }}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                      }}
                      autoFocus
                      className="pl-9 h-8"
                    />
                  </div>
                </div>

                {filteredTourOperators.length === 0 && (
                  <div className="p-3 text-sm text-gray-500">
                    No tour operators found
                  </div>
                )}
                {[...filteredTourOperators]
                  .sort((a, b) =>
                    a.officeName.localeCompare(b.officeName, undefined, {
                      sensitivity: "base",
                    })
                  )
                  .map((to) => (
                  <SelectItem
                    key={to.tourOperatorId}
                    value={to.tourOperatorId.toString()}
                  >
                    <div className="flex flex-col py-1">
                      <span className="font-medium">
                        {to.officeName}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {selectedTOId && !loadingLedger && ledgerTransactions.length === 0 && (
          <Card
            className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
            style={{ gap: "0px", marginTop: "10px" }}
          >
            <p className="text-sm text-gray-500 mt-3">
              <div className="py-16 text-center text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="font-medium">No transactions found</p>
                <p className="text-sm text-gray-400 mt-1">
                  Try adjusting your filters
                </p>
              </div>
            </p>
          </Card>
        )}

        {!selectedTOId && (
          <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg" style= {{ marginTop: "10px" }}>
            <CardContent className="p-12 text-center">
              <Wallet className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">
                Please select a Tour Operator to view their ledger
              </p>
            </CardContent>
          </Card>
        )}

        {/* {selectedTOId &&(
                <div className="py-16 text-center text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="font-medium">No transactions found</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Try adjusting your filters first
                      </p>
                    </div>
              )} */}

        {selectedTOId && loadingLedger && (
          <div className="flex flex-col items-center justify-center gap-3">
            <span className="font-medium text-sm">
              <FullScreenLoading />
            </span>
          </div>
        )}

        {selectedTOId && !loadingLedger && hasLedgerData && (
          <>
            {/* Balance Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ marginTop: "10px" }}>
              <Card
                className="bg-gradient-to-br from-[#5B9BD5] to-[#4A8BC2] text-white shadow-xl"
                style={{ gap: "0px", marginTop: "0px" }}
              >
                <CardHeader className="flex flex-row items-center pb-2 p-4">
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <CardTitle className="text-sm text-white">
                      Current Balance
                    </CardTitle>
                    <Wallet className="w-4 h-4 text-white/80" />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl text-white">
                    {currencySymbol}
                    {displayedBalance.toLocaleString()}
                  </div>
                  <p className="text-xs text-white/70">Available balance</p>
                </CardContent>
              </Card>

              <Card
                className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
                style={{ gap: "0px" }}
              >
                <CardHeader className="flex flex-row items-center pb-2 p-4">
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <CardTitle className="text-sm" style={{ color: "#5B9BD5" }}>
                      Total Credit
                    </CardTitle>
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl text-green-600">
                    {currencySymbol}
                    {totalCredit.toLocaleString()}
                  </div>
                  <p className="text-xs text-gray-600">Money received</p>
                </CardContent>
              </Card>

              <Card
                className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
                style={{ gap: "0px" }}
              >
                <CardHeader className="flex flex-row items-center pb-2 p-4">
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <CardTitle className="text-sm" style={{ color: "#5B9BD5" }}>
                      Total Debit
                    </CardTitle>
                    <TrendingDown className="w-4 h-4 text-red-500" />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-2xl text-red-600">
                    {currencySymbol}
                    {totalDebit.toLocaleString()}
                  </div>
                  <p className="text-xs text-gray-600">Money spent</p>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-sm" style={{ marginTop: "10px" }}>
              <CardContent className="pt-6">
                <div
                  className={`grid grid-cols-1 gap-2 items-end ${
                    isFilterApplied ? "md:grid-cols-4" : "md:grid-cols-3"
                  }`}
                >
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Search transactions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-gray-300"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 whitespace-nowrap">
                      From Date:
                    </label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      placeholder="From Date"
                      className="border-gray-300"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600 whitespace-nowrap">
                      To Date:
                    </label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      placeholder="To Date"
                      className="border-gray-300"
                    />
                  </div>
                  {isFilterApplied && (
                    <Button
                      variant="outline"
                      onClick={resetFilters}
                      className="border-gray-300 text-gray-600 hover:bg-gray-100"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Ledger Table */}
            <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px", marginTop: "10px" }}>
              <CardHeader
                className="border-b bg-gradient-to-r from-sky-50 to-blue-50"
                style={{ gap: "0px" }}
              >
                <div className="flex items-center justify-between">
                  <CardTitle style={{ color: "#5B9BD5", paddingBottom: "24px" }}>
                    Transaction History
                  </CardTitle>
                  <Badge variant="secondary" className="text-sm">
                    {sortedTransactions.length} Transaction
                    {sortedTransactions.length !== 1 ? "s" : ""}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent style={{ padding: "0px 0px 0px 0px" }}>
                {/* {sortedTransactions.length === 0 ? (
                  <div className="text-center py-12">
                    <Receipt className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600">No transactions found</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Try adjusting your filters
                    </p>
                  </div>
                ) : ( */}
                <div className="divide-y divide-gray-100">
                  {sortedTransactions.length === 0 && (
                    <div className="py-10 text-center text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="font-medium">No transactions found</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Try adjusting your filters
                      </p>
                    </div>
                  )}
  
                  {sortedTransactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className="p-4 hover:bg-gray-50 transition-colors"
                    >
                      {/* Desktop View - Grid Layout */}
                      <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                        {/* Date & Icon - 3 cols */}
                        <div className="col-span-3 flex items-center gap-2">
                          <div
                            className={`p-2 rounded-lg ${transaction.type === "credit" ? "bg-green-100" : "bg-red-100"}`}
                          >
                            {transaction.type === "credit" ? (
                              <ArrowDownCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <ArrowUpCircle className="w-4 h-4 text-red-600" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600">
                            {transaction.date}
                          </p>
                        </div>

                        {/* Description + Badges - 5 cols */}
                        <div className="col-span-5">
                          <p className="text-sm text-gray-900 mb-1">
                            {transaction.description}
                          </p>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {transaction.applicationNo && (
                              <Badge className="text-xs bg-sky-100 text-sky-700 border-sky-300 px-2 py-0.5">
                                {transaction.applicationNo}
                              </Badge>
                            )}
                            {transaction.receiptNo && (
                              <Badge className="text-xs bg-green-100 text-green-700 border-green-300 px-2 py-0.5">
                                {transaction.receiptNo}
                              </Badge>
                            )}
                            {transaction.reference && (
                              <Badge className="text-xs bg-green-100 text-green-700 border-green-300 px-2 py-0.5">
                                {transaction.reference}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Debit Column - 2 cols */}
                        <div className="col-span-2 text-right">
                          {transaction.debit > 0 ? (
                            <p className="text-sm text-red-600 font-medium">
                              {currencySymbol}
                              {transaction.debit.toLocaleString()}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400">-</p>
                          )}
                        </div>

                        {/* Credit Column - 2 cols */}
                        <div className="col-span-2 text-right">
                          {transaction.credit > 0 ? (
                            <p className="text-sm text-green-600 font-medium">
                              {currencySymbol}
                              {transaction.credit.toLocaleString()}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400">-</p>
                          )}
                        </div>
                      </div>

                      {/* Mobile View - Card Layout */}
                      <div className="sm:hidden space-y-3">
                        {/* Header: Icon, Date, Type */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className={`p-2 rounded-lg ${transaction.type === "credit" ? "bg-green-100" : "bg-red-100"}`}
                            >
                              {transaction.type === "credit" ? (
                                <ArrowDownCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <ArrowUpCircle className="w-4 h-4 text-red-600" />
                              )}
                            </div>
                            <p className="text-xs text-gray-600">
                              {transaction.date}
                            </p>
                          </div>
                          <Badge
                            variant={
                              transaction.type === "credit"
                                ? "default"
                                : "destructive"
                            }
                            className="text-xs"
                          >
                            {transaction.type === "credit" ? "Credit" : "Debit"}
                          </Badge>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-gray-900">
                          {transaction.description}
                        </p>

                        {/* Badges */}
                        {(transaction.applicationNo ||
                          transaction.receiptNo ||
                          transaction.reference) && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {transaction.applicationNo && (
                              <Badge className="text-xs bg-sky-100 text-sky-700 border-sky-300 px-2 py-0.5">
                                {transaction.applicationNo}
                              </Badge>
                            )}
                            {transaction.receiptNo && (
                              <Badge className="text-xs bg-green-100 text-green-700 border-green-300 px-2 py-0.5">
                                {transaction.receiptNo}
                              </Badge>
                            )}
                            {transaction.reference && (
                              <Badge className="text-xs bg-yellow-100 text-yellow-700 border-yellow-300 px-2 py-0.5">
                                {transaction.reference}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Amount Details */}
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Debit</p>
                            {transaction.debit > 0 ? (
                              <p className="text-sm text-red-600 font-medium">
                                {currencySymbol}
                                {transaction.debit.toLocaleString()}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-400">-</p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Credit</p>
                            {transaction.credit > 0 ? (
                              <p className="text-sm text-green-600 font-medium">
                                {currencySymbol}
                                {transaction.credit.toLocaleString()}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-400">-</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {sortedTransactions.length > 0 && (
                    <>
                      {/* Total Row */}
                      <div className="mt-3 pb-3">
                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-t-2 border-gray-300">
                          <div className="p-4">
                            {/* Desktop Total Row */}
                            <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                              {/* Empty space for date/icon - 3 cols */}
                              <div className="col-span-3"></div>

                              {/* Total Label - 5 cols */}
                              <div className="col-span-5">
                                <p className="text-sm font-bold text-gray-900">
                                  TOTAL
                                </p>
                              </div>

                              {/* Total Debit - 2 cols */}
                              <div className="col-span-2 text-right">
                                <p className="text-sm text-red-700 font-bold">
                                  {currencySymbol}
                                  {totalDebit.toLocaleString()}
                                </p>
                              </div>

                              {/* Total Credit - 2 cols */}
                              <div className="col-span-2 text-right">
                                <p className="text-sm text-green-700 font-bold">
                                  {currencySymbol}
                                  {totalCredit.toLocaleString()}
                                </p>
                              </div>
                            </div>

                            {/* Mobile Total Row */}
                            <div className="sm:hidden space-y-3">
                              <p className="text-sm font-bold text-gray-900">
                                TOTAL
                              </p>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <p className="text-xs text-gray-600 mb-1">
                                    Total Debit
                                  </p>
                                  <p className="text-sm text-red-700 font-bold">
                                    {currencySymbol}
                                    {totalDebit.toLocaleString()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-600 mb-1">
                                    Total Credit
                                  </p>
                                  <p className="text-sm text-green-700 font-bold">
                                    {currencySymbol}
                                    {totalCredit.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Balance Row */}
                      <div>
                        <div className="bg-gradient-to-r from-sky-50 to-blue-100 border-t-2 border-[#5B9BD5]">
                          <div className="p-4">
                            {/* Desktop Balance Row */}
                            <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                              {/* Empty space - 3 cols */}
                              <div className="col-span-3"></div>

                              {/* Balance Label - 5 cols */}
                              <div className="col-span-5 flex items-center gap-3">
                                <div
                                  className="p-2 rounded-lg"
                                  style={{ backgroundColor: "#5B9BD5" }}
                                >
                                  <Wallet className="w-4 h-4 text-white" />
                                </div>
                                <p
                                  className="text-sm font-bold"
                                  style={{ color: "#5B9BD5" }}
                                >
                                  CURRENT BALANCE
                                </p>
                              </div>

                              {/* Empty - 2 cols */}
                              <div className="col-span-2"></div>

                              {/* Current Balance Amount - 2 cols */}
                              <div className="col-span-2 text-right">
                                <p
                                  className="text-sm font-bold"
                                  style={{ color: "#5B9BD5" }}
                                >
                                  {currencySymbol}
                                  {displayedBalance.toLocaleString()}
                                </p>
                              </div>
                            </div>

                            {/* Mobile Balance Row */}
                            <div className="sm:hidden flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div
                                  className="p-2 rounded-lg"
                                  style={{ backgroundColor: "#5B9BD5" }}
                                >
                                  <Wallet className="w-4 h-4 text-white" />
                                </div>
                                <p
                                  className="text-sm font-bold"
                                  style={{ color: "#5B9BD5" }}
                                >
                                  CURRENT BALANCE
                                </p>
                              </div>
                              <p
                                className="text-sm font-bold"
                                style={{ color: "#5B9BD5" }}
                              >
                                {currencySymbol}
                                {displayedBalance.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {/* {totalCount > itemsPerPage && ( */}
                  <Pagination
                    totalItems={filteredTransactions.length}
                    itemsPerPage={pageSize}
                    currentPage={pageNo}
                    onPageChange={(page) => setPageNo(page)}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setPageNo(1);
                    }}
                    label="transactions"
                  />
                {/* )} */}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </>
  );
};
